import { createEffect, createSignal, on, Show, useContext } from 'solid-js';
import { Text } from '../../ui-components/text/text';
import { Button } from '../../ui-components/button/button';
import { CoursePath, PathCategory } from '../course-path/course-path';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { gql } from 'graphql-request';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Heading } from '../../ui-components/heading/heading';
import { ShowpadCodeSubmission } from '../showpad-code-submission/showpad-code-submission';
import { Link } from '../../ui-components/link/link';
import { ErrorCatcher } from '../../tools/error-catcher';
import { User } from '../../types/shared';

const GET_PATHS_QUERY = gql`
    query GetPathsAndItemsByAccess {
        getPathsAndItemsByAccess {
            categoryName,
            paths {
                id
                name
                curriculums {
                    id
                    name
                    courses {
                        courseId
                        name
                        description
                        progress
                        learnMinutes
                        completedDate
                        coverImageUrl {
                            small
                        }
                        durationInSeconds
                        tags {
                            name
                        }
                        path {
                            id
                            name
                        }
                        curriculum {
                            id
                            name
                        }
                    }
                }
            }
        }
    }
`;

export const CourseList = () => {
    const {
        userState,
        localize,
        createCachedResource,
        contactUsPage,
        appStore,
    } = useContext(AppContext);
    
    const [pathsCategories, setPathsCategories] = createSignal<PathCategory[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [ pathCategoriesData ] = createCachedResource(GET_PATHS_QUERY, () => ({showpadCode: userState.user?.showpadCode}), true);


    const hasNoCode = () => userState.user && !userState.user.showpadCode;

    createEffect(
        on(
            () => userState.user,
            (newUser: User | null, prevUser: User | null | undefined) => {
                if (!prevUser && newUser?.uuid) {
                    setLoading(true);
                }
            },
            { defer: true }
        )
    );

    createEffect(() => {
        if (pathCategoriesData() && pathCategoriesData().getPathsAndItemsByAccess) {
            const pathsDataToSet = pathCategoriesData().getPathsAndItemsByAccess;
            setPathsCategories(pathsDataToSet);
            setLoading(false);
        }
    });

    const SubmitCodeFromProfileSection = () => (
        <div class="bg-white my-8 rounded-large flex flex-col gap-4 max-mobile:my-4 max-mobile:gap-6 max-mobile:rounded-small">
            <Heading tag='h3' variant='bigRed'>
                {localize(
                    'no-courses-available',
                    'Enter your Access code'
                )}
            </Heading>

            <div class="flex flex-col gap-14 max-w-[65rem]">
                <Text fontSize="normal">
                    {localize('code-no-access', 'Your current code does not give you access to any courses. Change it from your profile.')}
                </Text>
            </div>
        </div>
    );

    const HasNoCodeSection = () => (
        <div>
            <Text fontSize="normal">
                {localize(
                    'provide-valid-access-code',
                    'A valid access code is required to enter the E-Learning. If you do not have one, please reach out to your local contact person.'
                )}
            </Text>

            <div class="mb-6">
                <Link label={contactUsPage.title} url={contactUsPage.url} customCss={'margin-bottom: "1rem"'} />
            </div>
            <ShowpadCodeSubmission />
        </div>
    );

    const HasCodeButNoCourses = () => (
        <Show when={(!hasNoCode()) && (!pathsCategories() || pathsCategories()?.length === 0)}>
            <SubmitCodeFromProfileSection />
        </Show>
    );

    const HasCourses = () => (
        <Show when={pathsCategories()?.length > 0}>
            <CoursePath pathsCategories={pathsCategories()} />
        </Show>
    );

    return (
        <ErrorCatcher componentName='CourseList' logErrorToServer={true}>
            <div class="flex justify-center">
                <Show when={loading()}>
                    <LoadingSpinner />
                </Show>
            </div>

            <Show when={!loading()}>
                <Show when={userState.user}>
                    <HasCodeButNoCourses />
                    <HasCourses />
                </Show>

                <Show when={!userState.user}>
                    <div class="mb-[0.25em]">
                        <Heading tag='h3' variant='large' noBlockSpacing={true}>
                            {localize('login-to-view-courses', 'Please login to access')}
                        </Heading>
                    </div>

                    <Text fontSize="normal" displayRedVerticalLine={false}>
                        {localize('login-to-view-courses-paragraph', 'Once logged in, submit your E-Learning Access code to access courses, track your progress, and download certificates.')}
                    </Text>
                    <Button label={localize('showpad-login', 'Login')} variant='primary' onClick={() => appStore.openLoginMenu()} />
                </Show>


                <Show when={hasNoCode()}>
                    <HasNoCodeSection />
                </Show>
            </Show>

        </ErrorCatcher>
    );
};

